import { ChangeEvent, useEffect, useState } from "react";
import { IHeadCell, ITableConfig, ITableData, TableSortOrder } from "../../../../interfaces";
import { defined } from "../../../../utils/type-checks";
import { getComparator } from "../utils/getComparior";
import { searchByFilter } from "../utils/searchByFilter";
import { stableSort } from "../utils/stableSort";

const initialTableConfig: ITableConfig = {
	orderBy: '',
	order: TableSortOrder.Ascending,
	page: 0,
	rowsPerPage: 10,
	searchValue: '',
	selected: [],
	selectedToState: () => {
	}
}

export const useTableController = (headCells: IHeadCell[], data: ITableData[], customTableConfig?: ITableConfig, getSelectedCheckboxes?: (value: string[]) => void) => {

	const [order, setOrder] = useState<TableSortOrder>(customTableConfig?.order || defined(initialTableConfig.order));
	const [orderBy, setOrderBy] = useState<string>(customTableConfig?.orderBy || defined(initialTableConfig.orderBy));
	const [page, setPage] = useState<number>(customTableConfig?.page || defined(initialTableConfig.page));
	const [selected, setSelected] = useState<string[]>(customTableConfig?.selected || defined(initialTableConfig.selected));
	const [rowsPerPage, setRowsPerPage] = useState<number>(customTableConfig?.rowsPerPage || defined(initialTableConfig.rowsPerPage));
	const [searchValue, setSearchValue] = useState<string>(customTableConfig?.searchValue || defined(initialTableConfig.searchValue));
	const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

	useEffect(() => {
		if (customTableConfig?.selected) {
			setSelected(customTableConfig.selected);
			setIsAllSelected(customTableConfig.selected.length === data.length && data.length > 0);
		}
	}, [customTableConfig?.selected, data.length]);

	const filterChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setSearchValue(event.target.value)
	};

	const setOrderHandler = (order: TableSortOrder) => setOrder(order);

	const setOrderByHandler = (orderBy: string) => setOrderBy(orderBy);

	const changePageHandler = (event: unknown, newPage: number) => setPage(newPage);

	const setSelectedHandler = (value: string) => {
		const strValue = String(value);
		if (selected.includes(strValue)) {
			const newSelected = selected.filter(item => item !== strValue);
			setSelected(newSelected)
			getSelectedCheckboxes && getSelectedCheckboxes(newSelected)
			customTableConfig?.selectedToState?.(newSelected);
			setIsAllSelected(false)
		} else {
			const newSelected = [...selected, strValue];
			setSelected(newSelected)
			getSelectedCheckboxes && getSelectedCheckboxes(newSelected)
			customTableConfig?.selectedToState?.(newSelected);
			setIsAllSelected(newSelected.length === data.length && data.length > 0)
		}
	}

	const allSelectToggler = (allIds: string[]) => {
		const stringIds = allIds.map(String);
		if (selected.length === stringIds.length) {
			setSelected([])
			getSelectedCheckboxes && getSelectedCheckboxes([])
			customTableConfig?.selectedToState?.([]);
			setIsAllSelected(false)
		} else {
			setSelected(stringIds)
			getSelectedCheckboxes && getSelectedCheckboxes(stringIds)
			customTableConfig?.selectedToState?.(stringIds);
			setIsAllSelected(true)
		}
	}
	const changeRowPerPageHandler = (event: ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10))
		setPage(0)
	}

	const createSortHandler = (property: string) => {
		const isAsc = orderBy === property && order === TableSortOrder.Ascending;
		setOrderHandler(isAsc ? TableSortOrder.Descending : TableSortOrder.Ascending);
		setOrderByHandler(property);
	}

  const isSelectedHandler = (id: string) => selected.indexOf(String(id)) !== -1;

	const drawArray = (() => stableSort(data, getComparator(order, orderBy), orderBy === 'updatedAt' || orderBy === 'createdAt')
		.filter(searchByFilter(searchValue, headCells)))()


	return {
		tableConfig: {
			order,
			orderBy,
			page,
			rowsPerPage,
			searchValue
		},
		filterChangeHandler,
		setOrderHandler,
		setOrderByHandler,
		changePageHandler,
		changeRowPerPageHandler,
		setSelectedHandler,
		allSelectToggler,
		isAllSelected,
		isSelectedHandler,
		createSortHandler,
		drawArray
	}
}